import { type Channel, FeeTestResult, formatBRL, formatBps, formatIsoDate } from '@deizi/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field, Seg } from '../../../components/Form';
import { api } from '../../../lib/api';
import { parseCentavos } from '../../../lib/numbers';

/** "Testar tarifa": every fee a channel takes at a typed price. */
export function FeeTester({
  channels,
  initialChannelId,
}: {
  channels: Channel[];
  initialChannelId: string | null;
}) {
  const [channelId, setChannelId] = useState<string | null>(initialChannelId);
  const [price, setPrice] = useState('54,90');
  const [version, setVersion] = useState<'current' | 'next'>('current');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [payment, setPayment] = useState<string | null>(null);

  const channel = channels.find((c) => c.id === channelId) ?? channels[0] ?? null;
  const next =
    channel?.versions.find((v) => v.state === 'draft' || v.state === 'scheduled') ?? null;
  const versionId = version === 'next' && next ? next.id : channel?.current?.id;
  const priceCentavos = parseCentavos(price);
  const payable = channel?.paymentMethods.filter((m) => m.feeBps !== null) ?? [];

  const result = useQuery({
    queryKey: ['fee-test', channel?.id, versionId, priceCentavos, categoryId, payment],
    enabled: Boolean(channel && versionId && priceCentavos !== null),
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.post('/channels/fee-test', FeeTestResult, {
        channelId: channel!.id,
        versionId,
        priceCentavos,
        ...(categoryId ? { categoryId } : {}),
        ...(payment ? { paymentMethod: payment } : {}),
      }),
  });
  const r = result.data;
  const rangeText = r?.fixedFee.range
    ? r.fixedFee.range.toCentavos === null
      ? `a partir de ${formatBRL(r.fixedFee.range.fromCentavos)}`
      : `${formatBRL(r.fixedFee.range.fromCentavos)} a ${formatBRL(r.fixedFee.range.toCentavos)}`
    : 'sem tarifa fixa';

  if (!channel) return null;
  return (
    <section className="card col" style={{ gap: 14 }} aria-live="polite">
      <div className="row between wrap">
        <h2>Testar tarifa</h2>
        <span className="sm muted">Digite um preço e veja cada tarifa que se aplica</span>
      </div>
      <div className="row wrap" style={{ gap: 8 }} role="radiogroup" aria-label="Canal do teste">
        <span className="lbl" style={{ marginRight: 4 }}>
          Canal
        </span>
        {channels.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={c.id === channel.id}
            className={c.id === channel.id ? 'chip on' : 'chip'}
            onClick={() => {
              setChannelId(c.id);
              setCategoryId(null);
              setPayment(null);
              setVersion('current');
            }}
          >
            <span className="dot" style={{ background: c.color }} />
            {c.name}
          </button>
        ))}
      </div>
      <div className="row wrap" style={{ gap: 14, alignItems: 'flex-end' }}>
        <Field id="tp" label="Preço de venda" style={{ width: 180 }}>
          <AffixInput
            id="tp"
            prefix="R$"
            value={price}
            onChange={setPrice}
            invalid={priceCentavos === null}
          />
        </Field>
        <Seg
          label="Versão"
          value={version}
          onChange={setVersion}
          options={[
            {
              value: 'current',
              label: channel.current ? `v${channel.current.number} · hoje` : 'hoje',
            },
            ...(next
              ? [
                  {
                    value: 'next' as const,
                    label: `v${next.number} · a partir de ${formatIsoDate(next.effectiveFrom).slice(0, 5)}`,
                  },
                ]
              : []),
          ]}
        />
      </div>
      {channel.categories.length > 0 ? (
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="lbl">Categoria</span>
          <button
            type="button"
            className={categoryId === null ? 'chip on' : 'chip'}
            onClick={() => setCategoryId(null)}
          >
            Padrão ({formatBps(channel.current?.commissionBps ?? 0)})
          </button>
          {channel.categories.map((k) => (
            <button
              key={k.id}
              type="button"
              className={categoryId === k.id ? 'chip on' : 'chip'}
              onClick={() => setCategoryId(k.id)}
            >
              {k.name} ({k.commissionBps === null ? 'sem valor' : formatBps(k.commissionBps)})
            </button>
          ))}
        </div>
      ) : null}
      {payable.length > 0 ? (
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="lbl">Pagamento</span>
          {payable.map((m) => (
            <button
              key={m.name}
              type="button"
              className={(payment ?? payable[0]?.name) === m.name ? 'chip on' : 'chip'}
              onClick={() => setPayment(m.name)}
            >
              {m.name}
            </button>
          ))}
        </div>
      ) : null}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(140px,100%),1fr))',
          gap: 12,
        }}
      >
        <div className="card sunk" style={{ padding: 14 }}>
          <p className="xs muted">Comissão {r ? formatBps(r.commission.bps) : ''}</p>
          <p className="fr tab" style={{ fontSize: 22 }}>
            {r ? formatBRL(r.commission.centavos) : '—'}
          </p>
        </div>
        <div className="card sunk" style={{ padding: 14 }}>
          <p className="xs muted">Tarifa fixa</p>
          <p className="fr tab" style={{ fontSize: 22 }}>
            {r ? formatBRL(r.fixedFee.centavos) : '—'}
          </p>
          <p className="xs muted">{r ? rangeText : ''}</p>
        </div>
        <div className="card sunk" style={{ padding: 14 }}>
          <p className="xs muted">Frete</p>
          <p className="fr tab" style={{ fontSize: 22 }}>
            {r ? formatBRL(r.shipping.centavos) : '—'}
          </p>
        </div>
        <div className="card sunk" style={{ padding: 14 }}>
          <p className="xs muted">Pagamento {r ? formatBps(r.payment.bps) : ''}</p>
          <p className="fr tab" style={{ fontSize: 22 }}>
            {r ? formatBRL(r.payment.centavos) : '—'}
          </p>
        </div>
        <div className="card" style={{ padding: 14, borderColor: 'var(--pe)' }}>
          <p className="xs muted">Você recebe</p>
          <p className="fr tab" style={{ fontSize: 22, color: 'var(--pe)' }}>
            {r ? formatBRL(r.youReceiveCentavos) : '—'}
          </p>
          <p className="xs muted">{r ? `${formatBps(r.feesShareBps)} do preço em tarifas` : ''}</p>
        </div>
      </div>
    </section>
  );
}
